<template>
  <Menu as="div" class="ml-1 relative inline-block text-left sm:flex-1 lg:flex-none lg:w-auto">
    <MenuButton
      class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
    >
      <div class="flex-1 text-left font-light flex gap-1">
        <span class="text-text-secondary">{{ $t('home.filters.mode_label') }}</span>
        <span v-if="selectedMod">{{ selectedMod.friendly_name }}</span>
        <span v-else>{{ $t('common.all') }}</span>
      </div>
      <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-50 mt-2 w-full lg:w-[192px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      >
        <MenuItem @click="(e) => { updatedSelectedMod(e, null); }" class="px-4 pt-3 hover:bg-gray-700">
          <a
            href="#"
            :class="[
              !selectedMod ? 'text-text-primary' : 'text-text-secondary',
              'flex items-center py-2 text-sm w-full justify-start font-light',
            ]"
          >
            {{ $t('home.filters.all_modes') }}
            <CheckIcon v-if="!selectedMod" class="h-5 w-5 ml-auto text-primary-400" />
          </a>
        </MenuItem>
        <MenuItem
          class="px-4 last:pb-3 hover:bg-gray-700"
          :key="mod"
          v-for="mod in mods"
          @click="(e) => { updatedSelectedMod(e, mod); }"
        >
          <a
            href="#"
            :class="[
              mod?.friendly_name === selectedMod?.friendly_name ? 'text-text-primary' : 'text-text-secondary',
              'flex items-center py-2 text-sm w-full justify-start',
            ]"
          >
            {{ mod.friendly_name }}
            <CheckIcon v-if="mod?.friendly_name === selectedMod?.friendly_name" class="h-5 w-5 ml-auto text-primary-400" />
          </a>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup>
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/solid';
import {
  ref, onMounted, defineEmits, defineExpose,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import currentModName from '../../utils/mod_renames';

const QUERY_PARAM_NAME = 'mod'; // e.g. `?mod=Quickfire`

const route = useRoute();
const router = useRouter();
const store = useStore();

const selectedMod = ref(null);
const emit = defineEmits(['selectedModChange']);
defineExpose({ selectedMod });

const props = defineProps({
  mods: {
    type: Array,
    required: true,
  },
});

function updatedSelectedMod(e, mod) {
  e.preventDefault();
  selectedMod.value = mod;
  updateQuery(mod);

  // NOTE(ssaunier): parent can use a ref and access exposed `selectedMod` to get the value, no need to emit it as an argument.
  emit('selectedModChange');
}

function updateQuery(mod) {
  const nextQuery = { ...route.query };
  if (mod) {
    nextQuery[QUERY_PARAM_NAME] = mod.name;
  } else {
    delete nextQuery[QUERY_PARAM_NAME];
  }
  router.replace({ query: nextQuery });
}

function setSelectedModFromQuery() {
  const modName = currentModName(route.query[QUERY_PARAM_NAME]);
  selectedMod.value = props.mods.find((mod) => mod.name === modName) || null;
  if (selectedMod.value) {
    emit('selectedModChange');
  }
}

onMounted(setSelectedModFromQuery);
</script>
